"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createGraphUtils = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const pointer_methods_1 = require("@finley-technologies/pointer-methods");
const createLoanProductShell_1 = require("../createLoanProductShell");
const dbNodes = __importStar(require("../dbNodes/dbNodes"));
const mesh = __importStar(require("../mesh"));
const tapeBuilder_1 = require("../tapeBuilder");
const typeguards = __importStar(require("../typeguards"));
const tasks_1 = require("../uiNodes/tasks");
const dbBasicGraphUtils_1 = require("./dbBasicGraphUtils");
__exportStar(require("./serialize"), exports);
const createGraphUtils = ({ id: artifactDocumentId, borrowerOrganizationId, implementationId, borrowerSlug, version, artifacts, cfIdToSlugMap, lpIdToSlugMap, dependencies, }) => {
    const basicGraph = (0, dbBasicGraphUtils_1.createBasicGraphUtils)({
        artifactDocumentId,
        version,
        artifacts,
        dependencies,
        cfIdToSlugMap,
        lpIdToSlugMap,
    });
    // APIS in this interface they must respect the scopedToCFSlugs.
    // if caller does not have access to a creditFacilitySlug,
    // the API must fail.
    const scopedToCFSlugs = [];
    const prefix = `[${borrowerSlug}][artifactDocumentId:${artifactDocumentId}]`;
    const _isRawTape = (artifactId) => {
        return basicGraph.getInputs(artifactId).length === 0;
    };
    // return all raw inputs required for given artifactId.
    const _getRawInputs = (artifactId) => {
        const rawInputs = basicGraph
            .getRecursiveInputs(artifactId)
            .filter(_isRawTape)
            .map(basicGraph.getArtifactById);
        return rawInputs;
    };
    const _getBBRNodes = () => {
        return artifacts.filter(dbNodes.borrowingBaseReport.isNode);
    };
    const _getGeneratedLoanTapeArtifacts = ({ loanProductSlug, }) => {
        const generatedLoanTapes = artifacts
            .filter((node) => dbNodes.generatedLoanTape.isNode(node))
            .filter(({ loanProductSlug: slug }) => slug === loanProductSlug);
        (0, common_1.finleyAssert)(generatedLoanTapes.length >= 0, `${prefix}: no generatedLoanTape found matching [loanProductSlug:${loanProductSlug}]`);
        return generatedLoanTapes;
    };
    // if scopedToCFSlugs is not provided, we assume that caller has access to all
    const limitScopeToCFSlugs = (cfSlugs) => {
        // copy over the array
        scopedToCFSlugs.length = 0;
        scopedToCFSlugs.push(...cfSlugs);
    };
    const assertCreditFacilityAccess = (creditFacilitySlug) => {
        (0, common_1.finleyAssert)(scopedToCFSlugs.includes(creditFacilitySlug), `${prefix}: caller does not have access to [creditFacilitySlug:${creditFacilitySlug}], [scopedToCFSlugs:${scopedToCFSlugs.join(',')}]`);
    };
    const hasRepaymentTape = (loanProductSlug) => {
        return (artifacts
            .filter(dbNodes.generatedRepaymentTape.isNode)
            .filter((artifact) => artifact.loanProductSlug === loanProductSlug)
            .length > 0);
    };
    const getAssetVerificationConfigsForCF = ({ creditFacilitySlug, }) => {
        assertCreditFacilityAccess(creditFacilitySlug);
        // find generatedInvoice node for matching params.
        const assetVerificationConfigs = artifacts
            .filter(dbNodes.receivableInvoice.isNode)
            .filter((artifact) => artifact.creditFacilitySlug === creditFacilitySlug)
            .map((artifact) => {
            const assetVerificationConfig = artifact.configuration.assetVerificationConfig;
            return {
                loanProductSlug: artifact.loanProductSlug,
                assetVerificationConfig,
            };
        });
        return assetVerificationConfigs;
    };
    const maybeGetAssetVerificationConfig = ({ loanProductSlug, creditFacilitySlug, }) => {
        assertCreditFacilityAccess(creditFacilitySlug);
        // find generatedInvoice node for matching params.
        const matchingConfigs = getAssetVerificationConfigsForCF({
            creditFacilitySlug,
        }).filter((config) => config.loanProductSlug === loanProductSlug);
        if (matchingConfigs.length === 0) {
            // its possible that CF/LP is not configured for asset verification.
            return null;
        }
        // we must find only 1 matching config
        (0, common_1.finleyAssert)(matchingConfigs.length === 1, `${prefix}: found [multiple:${matchingConfigs.length}] assetVerificationConfigs for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
        return matchingConfigs[0].assetVerificationConfig;
    };
    // TODO(jon): lot of assumptions to clean up in this method. This really only works for Atalaya
    //  which is single loan product and uses XLSX for receivable documentation.
    const getReceivableDocumentationUploadDestination = ({ creditFacilitySlug, filename, }) => {
        const invoiceArtifacts = artifacts
            .filter(dbNodes.receivableInvoice.isNode)
            .filter((artifact) => artifact.creditFacilitySlug === creditFacilitySlug);
        // TODO(jon): this assumption will not be correct for multi loan product.
        //  The UI functionality will be hidden behind LD flag showReceivableDocumentationUpload
        //  (enabled only for non-MLP customers) until we fix this assumption.
        (0, common_1.finleyAssert)(invoiceArtifacts.length === 1, `${prefix}: Found ${invoiceArtifacts.length} receivableInvoice for [creditFacilitySlug:${creditFacilitySlug}], expected 1.`);
        const invoiceArtifact = invoiceArtifacts[0];
        const rawDocumentationArtifacts = basicGraph
            .getRecursiveInputs(invoiceArtifact.id)
            .map((artifactId) => basicGraph.getArtifactById(artifactId))
            .filter(({ type }) => type === 'rawReceivableDocumentation');
        (0, common_1.finleyAssert)(rawDocumentationArtifacts.length === 1, `${prefix}: Found ${rawDocumentationArtifacts.length} rawReceivableDocumentation for [creditFacilitySlug:${creditFacilitySlug}], expected 1.`);
        const documentationArtifact = rawDocumentationArtifacts[0];
        let baseFilename;
        let extension;
        const extensionSeparatorIndex = filename.lastIndexOf('.');
        if (extensionSeparatorIndex === -1) {
            baseFilename = filename;
            extension = '';
        }
        else {
            baseFilename = filename.slice(0, extensionSeparatorIndex);
            extension = filename.slice(extensionSeparatorIndex);
        }
        // TODO(jon): this assumption will be incorrect for facilities that care about filename (i.e.,
        //  facilities that expect PDF receivable documentation). The trailing slash is used for
        //  xlsx documentation where any file uploaded with that prefix will be treated as the latest
        //  docs. I.e., Atalaya.
        (0, common_1.finleyAssert)(documentationArtifact.externalFilename?.endsWith('/'), `Expected documentation artifact [artifactId: ${documentationArtifact.id}] externalFilename to end with '/'`);
        const finleyKey = `${documentationArtifact.externalFilename}${baseFilename}-${new Date().toISOString()}${extension}`;
        return { finleyBucket: 'customerRawData', finleyKey };
    };
    const getReceivableInvoiceArtifact = ({ rawDocumentationArtifactId, }) => {
        // asset verification graph starts from `rawReceivableDocumentation` and ends in receivableInvoice
        // LP and CF are configured in receivableInvoice node.
        // this helper returns receivableInvoice artifact given rawReceivableDocumentation node id.
        // ensure that given artifactId is of type rawReceivableDocumentation
        const artifact = basicGraph.getArtifactById(rawDocumentationArtifactId);
        (0, common_1.finleyAssert)(dbNodes.rawReceivableDocumentation.isNode(artifact), `${prefix}: [artifactId:${rawDocumentationArtifactId}] is not of type rawReceivableDocumentation`);
        // walk the graph to find receivableInvoice node.
        const receivableInvoiceArtifacts = basicGraph
            .getRecursiveOutputs(rawDocumentationArtifactId)
            .map((artifactId) => basicGraph.getArtifactById(artifactId))
            .filter(dbNodes.receivableInvoice.isNode);
        // assumption here is that rawReceivableDocumentation will generate only 1 receivableInvoice node.
        (0, common_1.finleyAssert)(receivableInvoiceArtifacts.length === 1, `${prefix}: found [${receivableInvoiceArtifacts.length}] receivableInvoice nodes for [rawDocumentationArtifactId:${rawDocumentationArtifactId}]`);
        const receivableInvoiceArtifact = receivableInvoiceArtifacts[0];
        // auth check
        // caller can see this artifact
        // only if they have  access to the creditFacilitySlug.
        (0, common_1.finleyAssert)(receivableInvoiceArtifact.creditFacilitySlug, `${prefix}: creditFacilitySlug is required for [artifactId:${receivableInvoiceArtifact.id}]`);
        assertCreditFacilityAccess(receivableInvoiceArtifact.creditFacilitySlug);
        return receivableInvoiceArtifact;
    };
    const getGeneratedLoanTapeArtifact = ({ loanProductSlug, creditFacilitySlug, }) => {
        // find generatedLoanTape node for matching params.
        let matchingArtifacts = _getGeneratedLoanTapeArtifacts({
            loanProductSlug,
        });
        if (matchingArtifacts.length > 1) {
            // if we found multiple generated loan tapes, we must find the one
            // that matches the creditFacilitySlug.
            matchingArtifacts = matchingArtifacts.filter((artifact) => {
                return artifact.creditFacilitySlug === creditFacilitySlug;
            });
        }
        // we must find only 1 matching artifact
        (0, common_1.finleyAssert)(matchingArtifacts.length === 1, `${prefix}: found [multiple:${matchingArtifacts.length}] generatedLoanTape artifacts for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
        return matchingArtifacts[0];
    };
    /**
     * finds generatedLoanTape configuration, and returns TapeDefinition.
     */
    const getGeneratedLoanTapeDefinitionAndConfig = ({ loanProductSlug, creditFacilitySlug, }) => {
        const generatedLoanTapeArtifact = getGeneratedLoanTapeArtifact({
            loanProductSlug,
            creditFacilitySlug,
        });
        const instanceBuilder = (0, tapeBuilder_1.createTapeBuilder)(artifacts);
        const tapeDefinitionResult = instanceBuilder.generateTapeDefinition(generatedLoanTapeArtifact.id);
        if (!tapeDefinitionResult.isValid) {
            (0, common_1.debugLog)('Invalid tape definition in getGeneratedLoanTapeDefinitionAndConfig:', JSON.stringify(tapeDefinitionResult, null, 2));
        }
        (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, `${prefix}: failed to generate tape definition for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
        const tapeDefinition = tapeDefinitionResult.data;
        (0, common_1.finleyAssert)(tapeDefinition, `${prefix}: tapeDefinition was null for generated loan tape artifact [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
        return {
            tapeDefinition,
            config: instanceBuilder.getGeneratedLoanTapeConfig(generatedLoanTapeArtifact.id),
        };
    };
    const getAssignedLoanTapes = ({ creditFacilitySlug, }) => {
        const allAssignedLoanTapes = artifacts.filter(dbNodes.assignedLoanTape.isNode);
        // if no specific creditFacilitySlug is provided, return all assignedLoanTapes.
        if (creditFacilitySlug === null) {
            return allAssignedLoanTapes;
        }
        return allAssignedLoanTapes.filter((artifact) => artifact.creditFacilitySlug === creditFacilitySlug);
    };
    const getLPIdsforCreditFacility = ({ creditFacilitySlug, }) => {
        assertCreditFacilityAccess(creditFacilitySlug);
        // assignedLoanTape configures both CF and LP.
        const lpIds = getAssignedLoanTapes({
            creditFacilitySlug,
        }).map(({ loanProductSlug }) => basicGraph.lpSlugToId(loanProductSlug));
        return lpIds;
    };
    const updateLoanProductMechanics = ({ orgSlug, creditFacilityId, loanProductMechanics, multiLoanProductConcentrationLimits, addRemoveLPs, }) => {
        const creditFacilitySlug = basicGraph.cfIdToSlug(creditFacilityId);
        const errors = [];
        const lpIdsPerTapeBuilder = getLPIdsforCreditFacility({
            creditFacilitySlug,
        });
        const newLoanProductMechanics = loanProductMechanics.filter(({ _id_ }) => {
            // remove any loanProducts not configured in the graph
            // Note: we go by ids here, because the graph will not have slugs for
            // unreferenced loanProduct Ids.
            if (!lpIdsPerTapeBuilder.includes(_id_)) {
                if (addRemoveLPs) {
                    errors.push(`auto-removed [loanProductId:${_id_}]`);
                    return false;
                }
                errors.push(`[loanProductId:${_id_}}] is not configured in tape-builder.`);
            }
            return true;
        });
        const lpIdsPerCA = loanProductMechanics.map(({ _id_ }) => _id_);
        // add any loan products missing from ca.
        lpIdsPerTapeBuilder.forEach((loanProductId) => {
            if (!lpIdsPerCA.includes(loanProductId)) {
                const lpSlug = basicGraph.lpIdToSlug(loanProductId);
                if (addRemoveLPs) {
                    const loanProductShell = (0, createLoanProductShell_1.createLoanProductShell)({
                        loanProductId,
                        orgSlug,
                    });
                    newLoanProductMechanics.push(loanProductShell);
                    errors.push(`auto-added [loanProductSlug:${lpSlug}].`);
                }
                else {
                    errors.push(`[loanProductSlug:${lpSlug}] is not configured in CA.`);
                }
            }
        });
        // update loanProductMechanics with details from graph.
        const updatedLoanProductMechanics = newLoanProductMechanics.map((lpMechanics) => {
            const loanProductSlug = basicGraph.lpIdToSlug(lpMechanics._id_);
            const { tapeDefinition, config, } = getGeneratedLoanTapeDefinitionAndConfig({
                loanProductSlug,
                creditFacilitySlug,
            });
            const { baseFields } = tapeDefinition;
            const { externalReceivableId, signaledAssignmentValueMapping, externalAssignmentField, rawExternalReceivableIdField, } = config;
            // find signaledExternalAssignment for the given creditFacilitySlug
            let signaledExternalAssignment;
            if (signaledAssignmentValueMapping &&
                signaledAssignmentValueMapping.length) {
                const signaledExternalAssignmentFound = signaledAssignmentValueMapping.find((v) => v.creditFacilitySlug === creditFacilitySlug);
                (0, common_1.finleyAssert)(signaledExternalAssignmentFound, `${prefix}: signaledExternalAssignment not found for [creditFacilitySlug:${creditFacilitySlug}]`);
                (0, common_1.finleyAssert)(externalAssignmentField, 'externalAssignmentField not found');
                signaledExternalAssignment = {
                    signaledExternalAssignmentField: externalAssignmentField,
                    signaledExternalAssignment: signaledExternalAssignmentFound.fieldValue,
                };
            }
            const _config_ = {
                ...lpMechanics._config_,
                signaledExternalAssignment,
                rawExternalReceivableIdField: rawExternalReceivableIdField || undefined,
                baseFieldConfigs: baseFields,
                externalReceivableIdField: externalReceivableId,
            };
            return {
                ...lpMechanics,
                _config_,
            };
        });
        const lpIdsPerTapeBuilderSet = new Set(lpIdsPerTapeBuilder);
        multiLoanProductConcentrationLimits.forEach((mlpcl) => {
            mlpcl.loanProducts.forEach((lp) => {
                const lpId = lp._id_;
                if (!lpIdsPerTapeBuilderSet.has(lpId)) {
                    errors.push(`Multi loan product concentration limit ${mlpcl.name} references [loanProductId:${lpId}], but this loan product is not configured in tape-builder.`);
                }
            });
        });
        return {
            loanProductMechanics: updatedLoanProductMechanics,
            multiLoanProductConcentrationLimits,
            errors,
        };
    };
    const updateCalcAgentMechanics = ({ orgSlug, creditFacilityId, calcAgentMechanics, addRemoveLPs, }) => {
        const { loanProductMechanics, errors } = updateLoanProductMechanics({
            orgSlug,
            creditFacilityId,
            loanProductMechanics: calcAgentMechanics.loanProductMechanics,
            addRemoveLPs,
            multiLoanProductConcentrationLimits: calcAgentMechanics.multiLoanProductConcentrationLimits,
        });
        return {
            calcAgentMechanics: {
                ...calcAgentMechanics,
                _artifactDocumentId_: artifactDocumentId,
                loanProductMechanics,
            },
            errors,
        };
    };
    /**
     * returns Map(cfSlug => Map(rawArtifactSlug => LoanTapeSanitizeConfig[]))
     * where tapeType is one rawLoanTape
     * For now this only deals with loanTapes.
     */
    const getSanitizeDetails = () => {
        // find bbr nodes that caller has access to.
        const bbrNodes = _getBBRNodes().filter(({ creditFacilitySlug }) => scopedToCFSlugs.includes(creditFacilitySlug));
        const cfSlugToSanitizeConfigsMap = new Map();
        bbrNodes.forEach((bbrNode) => {
            const rawArtifactSlugToSanitizeConfigMap = new Map();
            // get all generated loan tape nodes that go as inputs to selected bbrNodes.
            const generatedLoanTapeNodes = basicGraph
                .getRecursiveInputs(bbrNode.id)
                .map(basicGraph.getArtifactById)
                .filter(({ type }) => type === 'generatedLoanTape');
            generatedLoanTapeNodes.forEach((generatedLoanTapeNode) => {
                (0, common_1.finleyAssert)(dbNodes.generatedLoanTape.isNode(generatedLoanTapeNode), `dbNodes.generatedLoanTape.isNode failed: [artifactId:${generatedLoanTapeNode.id}]`);
                const { loanProductSlug, configuration: { rawExternalReceivableIdField, externalReceivableId }, } = generatedLoanTapeNode;
                (0, common_1.finleyAssert)(loanProductSlug, `loanProductSlug is required for generatedLoanTape: ${generatedLoanTapeNode.artifactSlug}`);
                // find all raw tape nodes that go as inputs to selected bbrNodes.
                const rawLoanTapeInputs = _getRawInputs(generatedLoanTapeNode.id).filter(({ type }) => type === 'rawLoanTape');
                rawLoanTapeInputs.forEach((rawLoanTape) => {
                    // we are interested in sanitize operations that
                    // are in the path starting from rawLoanTape to generatedLoanTape.
                    // like:
                    // ============================
                    // rawLoanTape => ... => *sanitize*  => ...=> generatedLoanTape
                    // ============================
                    // to find those we will look for intersection of santitize nodes
                    // that are intersection of
                    //  a) inputs to generatedLoanTape
                    //  b) outputs from rawLoanTape.
                    const inputSanitizeNodes = basicGraph
                        .getRecursiveInputs(generatedLoanTapeNode.id)
                        .map(basicGraph.getArtifactById)
                        .filter(({ configuration }) => {
                        return typeguards.isTapeBuilderSanitizeTask(configuration);
                    })
                        .map(({ id }) => id);
                    const outputSanitizeNodes = basicGraph
                        .getRecursiveOutputs(rawLoanTape.id)
                        .map(basicGraph.getArtifactById)
                        .filter(({ configuration }) => {
                        return typeguards.isTapeBuilderSanitizeTask(configuration);
                    })
                        .map(({ id }) => id);
                    const sanitizeNodes = inputSanitizeNodes.filter((id) => outputSanitizeNodes.includes(id));
                    const sanitizeConfigs = sanitizeNodes.map((sanitizeNode) => {
                        const sanitizeArtifact = basicGraph.getArtifactById(sanitizeNode);
                        const { artifactSlug, configuration } = sanitizeArtifact;
                        (0, common_1.finleyAssert)(typeguards.isTapeBuilderSanitizeTask(configuration), 'typeguard.isTapeBuilderSanitizeTask failed');
                        const { baseFields } = configuration;
                        return {
                            sanitizeArtifactSlug: artifactSlug,
                            baseFields,
                            loanProductSlug,
                            rawExternalReceivableIdField: rawExternalReceivableIdField || externalReceivableId,
                        };
                    });
                    rawArtifactSlugToSanitizeConfigMap.set(rawLoanTape.artifactSlug, {
                        artifactType: rawLoanTape.type,
                        sanitizeConfigs,
                    });
                });
            });
            cfSlugToSanitizeConfigsMap.set(bbrNode.creditFacilitySlug, rawArtifactSlugToSanitizeConfigMap);
        });
        return cfSlugToSanitizeConfigsMap;
    };
    const updateSanitizeDetails = (updatedSanitizeConfigs) => {
        const sanitizeDetails = getSanitizeDetails();
        // collect all sanitizeArtifactSlug from the  sanitizeDetails
        const allSanitizeArtifactSlugs = Array.from(sanitizeDetails.values()).flatMap((map) => Array.from(map.values()).flatMap((v) => v.sanitizeConfigs.map((c) => c.sanitizeArtifactSlug)));
        const givenSanitizeArtifactSlugs = updatedSanitizeConfigs.map(({ sanitizeArtifactSlug }) => sanitizeArtifactSlug);
        // ensure that givenSanitizeArtifactSlugs are subset of allSanitizeArtifactSlugs
        const invalidSanitizeArtifactSlugs = givenSanitizeArtifactSlugs.filter((slug) => !allSanitizeArtifactSlugs.includes(slug));
        (0, common_1.finleyAssert)(invalidSanitizeArtifactSlugs.length === 0, `${prefix}: found invalid sanitizeArtifactSlugs [${invalidSanitizeArtifactSlugs.join(',')}]`);
        const updatedArtifacts = artifacts.map((artifactWithSlug) => {
            const updatedBaseFields = updatedSanitizeConfigs.find(({ sanitizeArtifactSlug }) => sanitizeArtifactSlug === artifactWithSlug.artifactSlug)?.baseFields;
            if (updatedBaseFields) {
                return {
                    ...artifactWithSlug,
                    configuration: {
                        ...artifactWithSlug.configuration,
                        baseFields: updatedBaseFields,
                    },
                };
            }
            return artifactWithSlug;
        });
        return updatedArtifacts;
    };
    const getBorrowerConfig = () => {
        const borrowerConfigArtifacts = artifacts.filter(dbNodes.configTape.isNode);
        // assumption here is that there will be only 1 config node.
        (0, common_1.finleyAssert)(borrowerConfigArtifacts.length <= 1, `${prefix}: Found ${borrowerConfigArtifacts.length} borrowerConfig, expected 1.`);
        return borrowerConfigArtifacts.length === 0
            ? null
            : borrowerConfigArtifacts[0].configuration.borrowerConfig;
    };
    const _resolveCreditFacilityConfig = (creditFacilityConfig) => {
        const { cashBalancesFeedMethodPointer, ...rest } = creditFacilityConfig;
        const resolvedConfig = {
            ...rest,
            cashBalancesFeedPointerMethod: cashBalancesFeedMethodPointer
                ? (0, pointer_methods_1.resolveCashBalanceFeedPointer)(cashBalancesFeedMethodPointer)
                : null,
        };
        return resolvedConfig;
    };
    const getCreditFacilityConfig = (creditFacilitySlug) => {
        const borrowerConfigArtifacts = artifacts.filter(dbNodes.configTape.isNode);
        // assumption here is that there will be only 1 config node.
        (0, common_1.finleyAssert)(borrowerConfigArtifacts.length <= 1, `${prefix}: Found ${borrowerConfigArtifacts.length} borrowerConfig, expected 1.`);
        if (borrowerConfigArtifacts.length === 0) {
            return null;
        }
        const node = borrowerConfigArtifacts[0];
        const config = node.configuration;
        const creditFacilityConfigs = config.creditFacilityConfigs;
        // find matching creditFacilityConfig
        const matchingConfig = creditFacilityConfigs.filter((cfConfig) => cfConfig.creditFacilitySlug === creditFacilitySlug);
        if (matchingConfig.length === 0) {
            // its possible that CF is not configured.
            return null;
        }
        (0, common_1.finleyAssert)(matchingConfig.length === 1, `${prefix}: found [multiple:${matchingConfig.length}] matching configs for [creditFacilitySlug:${creditFacilitySlug}]`);
        const sourceConfig = matchingConfig[0];
        const resolvedConfig = _resolveCreditFacilityConfig(sourceConfig);
        // find the artifact
        return resolvedConfig;
    };
    // For a given artifactId,
    // this function will return all artifactId that are between
    // given artifactId and a sanitizedNode.
    // this is useful to monitor the progress of the sanitization for a given raw ode
    const _getSanitizedArtifactIds = (artifactId) => {
        return basicGraph.bfsTraversal({
            startNodes: basicGraph.getOutputs(artifactId),
            search: 'outputs',
            criteria: (currentArtifactId) => {
                // check is current artifact is post tapeBuilderArtifact.
                const currentArtifact = basicGraph.getArtifactById(currentArtifactId);
                if (typeguards.isTapeBuilderTask(currentArtifact.configuration)) {
                    if ((0, tasks_1.isPostSanitizeOperation)(currentArtifact.configuration.operation)) {
                        // this operation is pos-sanitize, this is the node
                        // we want to wait for, and stop our search.
                        return {
                            matches: true,
                            continueBranch: false,
                        };
                    }
                    // this is a pre-sanitize operation (like pre-filter)
                    // we want to include this node to wait for, but
                    // not stop our search here.
                    return {
                        matches: true,
                        continueBranch: true,
                    };
                }
                // this is not a tape-builder node.
                // this is BBPL node like master loan tape
                // we do not want to include this and we do
                // not want to continue search in this path.
                return {
                    matches: false,
                    continueBranch: false,
                };
            },
        });
    };
    // returns upload config for the artifact.
    const getUploadConfig = ({ artifactId, }) => {
        const artifact = basicGraph.getArtifactById(artifactId);
        if (dbNodes.rawCashBalanceFeed.isNode(artifact) ||
            dbNodes.rawLoanTape.isNode(artifact) ||
            dbNodes.rawRepaymentTape.isNode(artifact) ||
            dbNodes.rawSupplementalTape.isNode(artifact)) {
            const uploadConfig = artifact.configuration.uploadConfig;
            if (!uploadConfig) {
                // this artifact is not configured for upload.
                return null;
            }
            return uploadConfig;
        }
        return null;
    };
    // returns DataPipeline.MeshInput corresponding to the artifactId or null
    const _getMeshInput = (artifactId) => {
        const artifact = basicGraph.getArtifactById(artifactId);
        if (dbNodes.rawLoanTape.isNode(artifact) ||
            dbNodes.rawRepaymentTape.isNode(artifact) ||
            dbNodes.rawSupplementalTape.isNode(artifact)) {
            // for simple file nodes, just get the data from uploadConfig.
            const uploadConfig = artifact.configuration.uploadConfig;
            if (uploadConfig) {
                const extension = uploadConfig.fileExtension;
                (0, common_1.finleyAssert)(extension, `extension was missing from uploadConfig for ${artifactId}`);
                const sanitizedArtifactIds = _getSanitizedArtifactIds(artifactId);
                return {
                    artifactId,
                    type: 'file',
                    extension,
                    baseFilename: uploadConfig.baseFilename,
                    friendlyName: uploadConfig.externalDisplayName,
                    loanProductSlug: artifact.loanProductSlug,
                    creditFacilitySlug: artifact.creditFacilitySlug,
                    sanitizedArtifactIds,
                };
            }
        }
        else if (dbNodes.rawCashBalanceFeed.isNode(artifact)) {
            // cash balance feed are special.
            // they may specify the input as tape or form or fetch
            const uploadConfig = artifact.configuration.uploadConfig;
            if (uploadConfig) {
                const cashUploadOptions = uploadConfig?.cashUploadOptions ?? [];
                if (cashUploadOptions.includes('tape')) {
                    const extension = uploadConfig.fileExtension;
                    (0, common_1.finleyAssert)(extension, `extension was missing from uploadConfig for ${artifactId}`);
                    const sanitizedArtifactIds = _getSanitizedArtifactIds(artifactId);
                    return {
                        artifactId,
                        type: 'file',
                        extension: 'csv',
                        baseFilename: uploadConfig.baseFilename,
                        loanProductSlug: artifact.loanProductSlug,
                        creditFacilitySlug: artifact.creditFacilitySlug,
                        friendlyName: uploadConfig.externalDisplayName,
                        sanitizedArtifactIds,
                    };
                }
                if (cashUploadOptions.includes('form')) {
                    // find the artifactId for the cashBalanceFeedContext
                    // this will be the artifact that is generated from the form.
                    const cashBalanceFeedContexts = basicGraph
                        .getRecursiveOutputs(artifactId)
                        .map(basicGraph.getArtifactById)
                        .filter(dbNodes.cashBalancesFeedContext.isNode)
                        .filter(({ creditFacilitySlug }) => creditFacilitySlug === artifact.creditFacilitySlug);
                    (0, common_1.finleyAssert)(cashBalanceFeedContexts.length === 1, `invalid cashBalanceFeedContext.length [${cashBalanceFeedContexts.length}] corresponding to rawCashBalanceFeed: ${artifactId}`);
                    const creditFacilitySlug = cashBalanceFeedContexts[0].creditFacilitySlug;
                    return {
                        artifactId: cashBalanceFeedContexts[0].id,
                        type: 'cashBalance',
                        creditFacilitySlug,
                        friendlyName: uploadConfig.externalDisplayName,
                        creditFacilityId: basicGraph.cfSlugToId(creditFacilitySlug),
                        sanitizedArtifactIds: [],
                    };
                }
            }
        }
        return null;
    };
    const _getMeshInputs = (artifactIds) => {
        return artifactIds
            .map(_getMeshInput)
            .filter(common_1.typeguards.isNotNullOrUndefined);
    };
    const getCfLpRelationships = () => {
        return getAssignedLoanTapes({ creditFacilitySlug: null })
            .map(({ loanProductSlug, creditFacilitySlug }) => {
            return {
                loanProductSlug,
                creditFacilitySlug,
            };
        })
            .reduce((acc, { loanProductSlug, creditFacilitySlug }) => {
            if (!acc[creditFacilitySlug]) {
                // eslint-disable-next-line no-param-reassign
                acc[creditFacilitySlug] = [];
            }
            acc[creditFacilitySlug].push(loanProductSlug);
            acc[creditFacilitySlug].sort();
            return acc;
        }, {});
    };
    const getLpCfRelationships = () => {
        const cfLpRelationships = getCfLpRelationships();
        return mesh.createLpToCfMap(cfLpRelationships);
    };
    /**
     * Creates groupings of associated loanProductSlugs and creditFacilitySlugs
     *
     * Slugs can be converted to ids with provided transforms in dbGraphUtils
     */
    const getLpCfGroupings = () => {
        // reverse map cfToLpMap for easy lookup
        const cfToLpMap = getCfLpRelationships();
        const lpToCfMap = getLpCfRelationships();
        return mesh.getLpCfGroupings({ cfToLpMap, lpToCfMap });
    };
    const toDataPipelineArtifact = (artifact) => {
        const { id, type, artifactSlug, configuration } = artifact;
        const uploadConfig = getUploadConfig({ artifactId: id });
        return {
            id,
            type,
            artifactSlug,
            configuration,
            externalDisplayName: uploadConfig?.externalDisplayName || artifactSlug,
        };
    };
    const _hasUploadConfig = (artifactId) => {
        const artifact = basicGraph.getArtifactById(artifactId);
        const uploadConfig = artifact.configuration.uploadConfig;
        return !!uploadConfig;
    };
    // partitions given array of meshes into BBPL and non-BBPL.
    // BBPL graphs are those that we want to surface in the datapipeline view.
    const _partitionGraphs = (graphs) => {
        const isBBPLGraph = (graph) => 
        // Graph is made up of one or more meshes (connected set of nodes).
        // Here we decide if we want to surface mesh to data-pipelines.
        // which will let customers choose the mesh and upload files against those.
        // we surface mesh if it has at-least one artifact with uploadConfig.
        graph.some(_hasUploadConfig);
        const bbpl = [];
        const nonBbpl = [];
        graphs.forEach((graph) => {
            if (isBBPLGraph(graph)) {
                bbpl.push(graph);
            }
            else {
                nonBbpl.push(graph);
            }
        });
        return { bbpl, nonBbpl };
    };
    // returns unique loanProducts and creditFacilities found in the nodes.
    const _getLpCfSlugsForArtifactIds = (artifactIds) => {
        const lpSlugs = new Set();
        const cfSlugs = new Set();
        artifactIds.forEach((artifactId) => {
            const artifact = basicGraph.getArtifactById(artifactId);
            if (artifact.loanProductSlug) {
                lpSlugs.add(artifact.loanProductSlug);
            }
            if (artifact.creditFacilitySlug) {
                cfSlugs.add(artifact.creditFacilitySlug);
            }
        });
        return { lpSlugs, cfSlugs };
    };
    // this function only converts.
    // caller must ensure the access to the artifactIds.
    const _toMeshDocument = (artifactIds) => {
        (0, common_1.finleyAssert)(artifactIds.length > 0, `${prefix}: artifactIds is empty`);
        // generate a stable id for the mesh document.
        // by combining the artifactDocumentId and 1st artifact id.
        const sortedArtifactIds = artifactIds.sort();
        const meshDocumentId = finley_types_1.DataPipeline.generateMeshDocumentId({
            artifactDocumentId,
            meshArtifactId: sortedArtifactIds[0],
        });
        const meshArtifacts = artifactIds
            .map(basicGraph.getArtifactById)
            .map(toDataPipelineArtifact);
        const meshDependencies = dependencies
            .filter(({ inputArtifactId, outputArtifactId }) => artifactIds.includes(inputArtifactId) &&
            artifactIds.includes(outputArtifactId))
            .map(({ inputArtifactId, outputArtifactId }) => ({
            source: inputArtifactId,
            target: outputArtifactId,
        }));
        const { lpSlugs, cfSlugs } = _getLpCfSlugsForArtifactIds(artifactIds);
        const meshInputs = _getMeshInputs(artifactIds);
        const fundingRequests = artifacts
            .filter(({ id }) => artifactIds.includes(id))
            .filter(dbNodes.fundingRequest.isNode)
            .map((artifact) => {
            const { creditFacilitySlug } = artifact;
            // find any userInputNeeded (inputType=verificationApproval) for the funding requests.
            // this will be set only on verification flows.
            const verificationApproval = artifacts.find((verificationApprovalArtifact) => {
                return (dbNodes.userInputNeeded.isNode(verificationApprovalArtifact) &&
                    verificationApprovalArtifact.configuration.inputType ===
                        'verificationApproval' &&
                    verificationApprovalArtifact.creditFacilitySlug ===
                        creditFacilitySlug);
            });
            return {
                artifactId: artifact.id,
                creditFacilitySlug,
                verificationArtifactId: verificationApproval?.id ?? null,
                creditFacilityId: basicGraph.cfSlugToId(creditFacilitySlug),
            };
        });
        const assignments = artifacts
            .filter(({ id }) => artifactIds.includes(id))
            .filter((artifact) => {
            return (dbNodes.userInputNeeded.isNode(artifact) &&
                artifact.configuration.inputType === 'assignement');
        })
            .map(({ id }) => ({ artifactId: id }));
        return {
            meshDocumentId,
            implementationId,
            artifacts: meshArtifacts,
            dependencies: meshDependencies,
            cfSlugs: Array.from(cfSlugs),
            lpSlugs: Array.from(lpSlugs),
            meshInputs,
            // for some nodes we render
            // special links in the upload
            fundingRequests,
            assignments,
            // mesh document need to know the borrower org.
            // as on lender dashboard they will be looking at multiple
            // borrowers.
            borrowerOrganizationId,
        };
    };
    // this function must be provided all artifactIds that are part of the mesh.
    const _canAccessArtifactIds = (artifactIds) => {
        const canAccess = artifactIds.every((id) => {
            const artifact = basicGraph.getArtifactById(id);
            return (!artifact.creditFacilitySlug ||
                scopedToCFSlugs.includes(artifact.creditFacilitySlug));
        });
        return canAccess;
    };
    // caller can access artifact if it has access to all the cfSlugs in the mesh.
    const canAccessArtifact = ({ artifactId, }) => {
        const graphs = basicGraph.splitDisjointGraphs();
        const artifactGraph = graphs.find((graph) => graph.includes(artifactId));
        (0, common_1.finleyAssert)(artifactGraph, `${prefix}: artifact not found in graph`);
        return _canAccessArtifactIds(artifactGraph);
    };
    const getScopedArtifact = ({ artifactId }) => {
        return canAccessArtifact({ artifactId })
            ? basicGraph.getArtifactById(artifactId)
            : null;
    };
    const getScopedArtifactIds = () => {
        const graphs = basicGraph.splitDisjointGraphs();
        const accessibleGraphs = graphs.filter(_canAccessArtifactIds);
        return accessibleGraphs.flatMap((graph) => graph);
    };
    const getScopedArtifactsOfType = ({ artifactType, }) => {
        const scopedArtifactIds = getScopedArtifactIds();
        return artifacts.filter((artifact) => scopedArtifactIds.includes(artifact.id) &&
            artifact.type === artifactType);
    };
    const getMeshDocuments = () => {
        const allGraphs = basicGraph.splitDisjointGraphs();
        const { bbpl: graphs } = _partitionGraphs(allGraphs);
        const callerGraphs = graphs.filter(_canAccessArtifactIds);
        return callerGraphs.map(_toMeshDocument);
    };
    const getMeshDocument = ({ meshDocumentId }) => {
        const meshDocuments = getMeshDocuments();
        const meshDocument = meshDocuments.find((document) => document.meshDocumentId === meshDocumentId);
        (0, common_1.finleyAssert)(meshDocument, `${prefix}: meshDocument not found`);
        return meshDocument;
    };
    const getMeshDocumentByArtifactId = ({ artifactId, }) => {
        const meshDocuments = getMeshDocuments();
        const meshDocument = meshDocuments.find(({ artifacts: meshArtifacts }) => meshArtifacts.some(({ id }) => id === artifactId));
        return meshDocument ?? null;
    };
    const resolveArtifactsByFilePath = ({ filepath, }) => {
        const allRaw = artifacts.filter((artifact) => artifact.externalFilename);
        const matches = allRaw.filter((artifact) => {
            const regex = `^${artifact.externalFilename}.*$`;
            return !!filepath.match(regex);
        });
        return { matches, allRaw };
    };
    return {
        ...basicGraph,
        // access control
        limitScopeToCFSlugs,
        canAccessArtifact,
        getScopedArtifactIds,
        getScopedArtifact,
        getScopedArtifactsOfType,
        hasRepaymentTape,
        // artifact specific stuff.
        getAssignedLoanTapes,
        getLPIdsforCreditFacility,
        // update mechanics.
        updateLoanProductMechanics,
        updateCalcAgentMechanics,
        // ========================
        // Node Config access
        // ========================
        // asset verification
        maybeGetAssetVerificationConfig,
        getReceivableDocumentationUploadDestination,
        getReceivableInvoiceArtifact,
        getAssetVerificationConfigsForCF,
        // get and set baseFields for sanitize nodes.
        getSanitizeDetails,
        updateSanitizeDetails,
        // fetch borrower config.
        getBorrowerConfig,
        getCreditFacilityConfig,
        // upload config
        getUploadConfig,
        // mesh
        getLpCfRelationships,
        getCfLpRelationships,
        getLpCfGroupings,
        getMeshDocument,
        getMeshDocuments,
        getMeshDocumentByArtifactId,
        // query
        resolveArtifactsByFilePath,
    };
};
exports.createGraphUtils = createGraphUtils;
//# sourceMappingURL=dbGraphUtils.js.map